import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { auth } from "../firebase/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import api from "services/api";
import linguistApi from "services/linguistApi";

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [linguistUserLoggedIn, setLinguistUserLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSigningOutRef = useRef(false);

  const signOutAndReset = useCallback(async (reasonLog) => {
    if (auth.currentUser && !isSigningOutRef.current) {
      isSigningOutRef.current = true;
      try {
        await signOut(auth);
      } catch (signOutError) {
        console.warn(reasonLog, signOutError);
      } finally {
        isSigningOutRef.current = false;
      }
    }

    setCurrentUser(null);
    setUserLoggedIn(false);
    setLinguistUserLoggedIn(false);
  }, []);

  const isLinguistRoute = useCallback(() => {
    if (typeof window === "undefined") return false;

    const path = String(window.location.pathname || "").toLowerCase();
    return path.startsWith("/linguist-login") || path.startsWith("/linguist-portal");
  }, []);

  const initializeUser = useCallback(async (user) => {
    if (!user) {
      setCurrentUser(null);
      setUserLoggedIn(false);
      setLinguistUserLoggedIn(false);
      setLoading(false);
      return;
    }

    // Linguist portal uses dedicated API auth checks and should not be forced
    // through /members/me bootstrap (which can 404 and sign users out).
    if (isLinguistRoute()) {
      try {
        const response = await linguistApi.get(`/me`);
        setCurrentUser({
          ...user,
          id: response.data?.id,
          email: response.data?.Email || user.email,
          Name: response.data?.Name,
          Role: "Linguist",
        });
        setUserLoggedIn(false);
        setLinguistUserLoggedIn(true);
      } catch (error) {
        const status = error.response?.status;
        const accountDisabled = error.response?.data?.accountDisabled;
        const userNotFound = error.response?.data?.error === "User not found in database";

        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid linguist account session:",
          );
        } else {
          setCurrentUser(user);
          setUserLoggedIn(false);
          setLinguistUserLoggedIn(false);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = await api.get(`/members/me`);
      setCurrentUser({
        ...user,
        id: response.data.id,
        email: response.data.Email,
        Role: response.data.Role,
        Name: response.data.Name,
        Image: response.data.Image,
        ImageThumbnail: response.data.ImageThumbnail,
        HourlyRate: response.data.HourlyRate,
      });
      setUserLoggedIn(true);
      setLinguistUserLoggedIn(false);
    } catch (error) {
      console.error("Auth initialization failed:", error);
      const status = error.response?.status;
      const accountDisabled = error.response?.data?.accountDisabled;
      const userNotFound = error.response?.data?.error === "User not found in database";

      if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
        await signOutAndReset(
          "Could not fully sign out invalid account session:",
        );
      } else {
        setCurrentUser(null);
        setUserLoggedIn(false);
        setLinguistUserLoggedIn(false);
      }
    } finally {
      setLoading(false);
    }
  }, [isLinguistRoute, signOutAndReset]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initializeUser);
    return unsubscribe;
  }, [initializeUser]);

  useEffect(() => {
    if (isLinguistRoute()) return;
    if (!currentUser?.id) return;

    const intervalId = setInterval(async () => {
      try {
        await api.get(`/members/me`);
      } catch (error) {
        const status = error.response?.status;
        const accountDisabled = error.response?.data?.accountDisabled;
        const userNotFound = error.response?.data?.error === "User not found in database";

        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid account session:",
          );
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [currentUser, isLinguistRoute, signOutAndReset]);

  useEffect(() => {
    if (!isLinguistRoute()) return;
    if (!auth.currentUser || !linguistUserLoggedIn) return;

    const intervalId = setInterval(async () => {
      try {
        await linguistApi.get(`/me`);
      } catch (error) {
        const status = Number(error?.response?.status || 0);
        const accountDisabled = error?.response?.data?.accountDisabled;
        const userNotFound =
          error?.response?.data?.error === "User not found in database";

        // Match member behavior: sign out only for invalid/disabled accounts,
        // do not force logout on transient auth/network failures.
        if ((status === 403 && accountDisabled) || status === 404 || userNotFound) {
          await signOutAndReset(
            "Could not fully sign out invalid linguist account session:",
          );
        }
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, [isLinguistRoute, signOutAndReset, currentUser, linguistUserLoggedIn]);

  const value = {
    currentUser,
    userLoggedIn,
    linguistUserLoggedIn,
    loading,
    setCurrentUser,
    setLinguistUserLoggedIn,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
